import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, School } from '@mui/icons-material';
import { Classe, Matiere } from '../../types';
import CurriculumApiService from '../../services/curriculumApi';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { formatDate } from '../../utils/dates';
import { logger } from '../../lib/logger';

interface ClasseDetailProps {
  classe: Classe;
}

const ClasseDetail: React.FC<ClasseDetailProps> = ({ classe }) => {
  const { environment } = useEnvironment();
  const [matieres, setMatieres] = useState<Matiere[]>([]);
  const [loadingMatieres, setLoadingMatieres] = useState(true);

  // Charger les matières appartenant à cette classe
  useEffect(() => {
    const loadMatieres = async () => {
      try {
        setLoadingMatieres(true);
        const allMatieres = await CurriculumApiService.getMatieres(environment);
        // Filtrer les matières qui appartiennent à cette classe
        const classeMatieres = allMatieres.filter(m => m.classeId === classe.id);
        setMatieres(classeMatieres);
      } catch (error) {
        logger.error('Erreur lors du chargement des matières:', error);
      } finally {
        setLoadingMatieres(false);
      }
    };

    loadMatieres();
  }, [classe.id, environment]);

  return (
    <Box>
      {/* En-tête */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          {classe.nom}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          <Chip
            label={classe.niveau}
            color="primary"
            size="small"
          />
          {classe.code && (
            <Chip
              label={classe.code}
              color="secondary"
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            label={(classe.statut || 'published') === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
            color={(classe.statut || 'published') === 'draft' ? 'warning' : 'success'}
            size="small"
          />
          <Chip
            label={classe.estActif ? 'Actif' : 'Inactif'}
            color={classe.estActif ? 'success' : 'default'}
            size="small"
            icon={classe.estActif ? <CheckCircle /> : <RadioButtonUnchecked />}
          />
          <Chip
            label={`Ordre: ${classe.ordre}`}
            color="default"
            size="small"
            variant="outlined"
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Informations générales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Informations générales
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {classe.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Niveau
                </Typography>
                <Typography variant="body2">{classe.niveau}</Typography>
              </Box>
              {classe.code && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Code
                  </Typography>
                  <Typography variant="body2">{classe.code}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Ordre
                </Typography>
                <Typography variant="body2">{classe.ordre}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Dates
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Date de création
                </Typography>
                <Typography variant="body2">
                  {formatDate(classe.dateCreation, 'dayMonthLong')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Dernière modification
                </Typography>
                <Typography variant="body2">
                  {formatDate(classe.dateModification, 'dayMonthLong')}
                </Typography>
              </Box>
              {classe.datePublication && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Date de publication
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(classe.datePublication, 'dayMonthLong')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Historique des statuts */}
      {classe.historiqueStatut && classe.historiqueStatut.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Historique des statuts
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {classe.historiqueStatut.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  borderLeft: 4,
                  borderColor: entry.statut === 'published' ? 'success.main' : 'warning.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Chip
                    label={entry.statut === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
                    color={entry.statut === 'draft' ? 'warning' : 'success'}
                    size="small"
                  />
                  <Typography variant="body2" color="textSecondary">
                    {formatDate(entry.date, 'dateTimeLong')}
                  </Typography>
                </Box>
                {entry.utilisateur && (
                  <Typography variant="body2" color="textSecondary">
                    Par: {entry.utilisateur}
                  </Typography>
                )}
                {entry.commentaire && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {entry.commentaire}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Description */}
      {classe.description && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Description
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {classe.description}
          </Typography>
        </Paper>
      )}

      {/* Matières de cette classe */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <School color="primary" />
          <Typography variant="h6" color="primary">
            Matières de cette classe ({matieres.length})
          </Typography>
        </Box>

        {loadingMatieres ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : matieres.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
            Aucune matière n'est associée à cette classe.
          </Typography>
        ) : (
          <List>
            {matieres.map((matiere) => (
              <ListItem
                key={matiere.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: matiere.couleurTheme,
                    borderRadius: 1,
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}
                >
                  {matiere.icone || '📚'}
                </Box>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">{matiere.nom}</Typography>
                      <Chip label={matiere.nomCourt} size="small" variant="outlined" />
                      <Chip label={matiere.code} size="small" color="secondary" variant="outlined" />
                      {!matiere.estActif && (
                        <Chip label="Inactif" size="small" color="default" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {matiere.description}
                      </Typography>
                      {matiere.heuresParSemaine && (
                        <Typography variant="caption" color="textSecondary">
                          {matiere.heuresParSemaine}h/semaine
                          {matiere.coefficientExamen && ` • Coef: ${matiere.coefficientExamen}`}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default ClasseDetail;
